/**
 * I passi del tutorial guidato dell'elenco Spedizioni.
 *
 * Un preparativo: il menu dei tag colorati sulla riga.
 * `stopShippingsListTour` rimette tutto com'era.
 *
 * I bersagli della pagina sono scoped; le voci del menu no, perche' Vuetify
 * stacca il contenuto dei menu e lo appende a `[data-app]`, fuori dalla pagina.
 */
const at = name => `[data-tour-scope="shippings-list"] [data-tour="${name}"]`;

const TAG_LIST = '[data-tour="ship-tag-list"]';

function isVisible(selector) {
	const element = document.querySelector(selector);

	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	/*
	 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
	 * Vuetify gli toglie subito `menuable__content__active`. Senza questo
	 * controllo un menu in chiusura sembrerebbe ancora aperto e il click di
	 * pulizia, invece di chiuderlo, lo riaprirebbe.
	 */
	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura, prima ancora che il pulsante facesse il
 * suo lavoro. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Aspetta che il bersaglio compaia o sparisca.
 *
 * L'attesa sta qui e non nel `timeout` del passo: cosi' si aspetta solo quando
 * c'e' davvero qualcosa da aprire, e a elenco vuoto il passo si aggancia subito
 * in basso invece di far aspettare a vuoto.
 */
async function waitFor(selector, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(selector) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

function click(selector) {
	const element = document.querySelector(selector);

	if (element) {
		element.click();
	}
}

async function openTagMenu() {
	await afterPendingClose();

	if (isVisible(TAG_LIST)) {
		return;
	}

	// Niente attivatore - elenco vuoto - non c'e' niente da aspettare.
	if (!isVisible(at('tag'))) {
		return;
	}

	click(at('tag'));
	await waitFor(TAG_LIST, true);
}

async function closeTagMenu() {
	await afterPendingClose();

	if (!isVisible(TAG_LIST)) {
		return;
	}

	// L'attivatore fa da interruttore: il secondo click richiude.
	click(at('tag'));
	await waitFor(TAG_LIST, false);
}


/** Rimette la pagina com'era: menu chiuso. */
export async function stopShippingsListTour() {
	await closeTagMenu();

}

export function createShippingsListTourSteps() {
	return [
		{
			body: "Questa pagina mette insieme due cose che di solito stanno separate: gli ordini da spedire e le spedizioni gia' partite. A sinistra trovi l'ordine, a destra la sua spedizione - corriere, etichetta, tracking, stato. Da qui si fa nascere una spedizione e da qui la si segue finche' non arriva a destinazione.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "La ricerca parte da tre caratteri e si lancia con Invio o con la lente: sotto quella soglia il filtro si azzera e torna l'elenco intero. Non si cerca mentre scrivi, apposta: l'elenco e' pesante e si ricarica solo quando glielo chiedi.",
			before: closeTagMenu,
			key: 'search',
			placement: 'bottom',
			target: at('search'),
			title: 'Cercare un ordine',
		},
		{
			body: "Sette filtri, tutti cumulabili. Data ordine lavora a intervallo; Platform, Payment e Status sono gli stessi dell'elenco ordini. Gli ultimi tre sono di questa pagina: Spedito separa cio' che e' gia' partito da cio' che aspetta ancora, Corriere restringe a un vettore, e Tags filtra sui pallini colorati che assegni tu dalle righe.",
			key: 'filters',
			placement: 'bottom',
			target: at('filters'),
			title: 'I filtri',
		},
		{
			body: "Le colonne si leggono in due blocchi. Da Data a Status sono i dati dell'ordine - occhio che gli stati sono due: Status Ex e' quello del marketplace, Status quello tuo interno. Da Corriere in poi sono i dati della spedizione, e restano vuoti finche' una spedizione non esiste. Se un ordine ne avesse piu' d'una, qui si vede sempre e solo la prima.",
			key: 'table',
			placement: 'bottom',
			/*
			 * L'intestazione, non la tabella intera: con quindici ordini la tabella e'
			 * piu' alta dello schermo e il ritaglio coprirebbe tutto il visibile,
			 * facendo sembrare sparito il velo.
			 */
			target: `${at('table')} thead`,
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: "I tre pallini a inizio riga raccolgono le azioni sull'ordine: Crea spedizione (sceglie colli e corriere e compra l'etichetta), Tracking ordine, e - se la spedizione esiste gia' - Tracking spedizione e Scarica etichetta. Ognuna apre una pagina; Indietro riporta qui con gli stessi filtri e la stessa pagina.",
			key: 'row-menu',
			placement: 'right',
			target: at('row-menu'),
			timeout: 250,
			title: 'Le azioni della riga',
		},
		{
			body: "Il piu' apre la riga e mostra due cose utili prima di spedire: i prodotti dell'ordine con sku, quantita' e prezzo, e sotto l'indirizzo di consegna per esteso. Ogni pezzo dell'indirizzo ha il suo pulsante copia - nome, via, comune, cap, provincia, paese, telefono - pensato per chi deve incollarli nel portale di un corriere.",
			key: 'expand',
			placement: 'right',
			target: at('expand'),
			timeout: 250,
			title: 'Prodotti e indirizzo di consegna',
		},
		{
			body: "Il camioncino accanto al numero d'ordine non e' il tracking del corriere: apre la scheda del tracking dell'ordine, dove il numero si inserisce o si corregge a mano. Serve quando la spedizione e' stata fatta fuori da qui - in posta, con un corriere non integrato - e il tracking va comunque registrato sull'ordine.",
			key: 'order-tracking',
			placement: 'right',
			target: at('order-tracking'),
			timeout: 250,
			title: 'Tracking inserito a mano',
		},
		{
			body: "Lo Stato e' quello che il corriere ci restituisce, e il colore dice se la pratica e' ancora viva: blu vuol dire che il sistema continua a interrogare il corriere per aggiornarla, verde che ha smesso perche' la corsa e' conclusa. Ultimo Agg. dice quand'e' arrivata l'ultima notizia.",
			key: 'ship-status',
			placement: 'top',
			target: at('ship-status'),
			timeout: 250,
			title: 'Lo stato della spedizione',
		},
		{
			body: "LDV e' la lettera di vettura, cioe' l'etichetta da stampare e attaccare sul pacco: l'icona la apre in una pagina a parte. Accanto, l'icona del tracking apre il dettaglio dei passaggi della spedizione, quello che il corriere ha registrato tappa per tappa.",
			key: 'label',
			placement: 'top',
			target: at('label'),
			timeout: 250,
			title: "L'etichetta e il dettaglio del tracking",
		},
		{
			body: "L'etichetta colorata a fine riga e' un promemoria visivo tuo: la clicchi, scegli un colore, e quel colore diventa filtrabile dal select Tags in alto. Non significa niente per il sistema - serve a te per marcare a occhio le righe da riprendere.",
			before: openTagMenu,
			key: 'tag',
			placement: 'left',
			target: TAG_LIST,
			timeout: 250,
			title: 'I tag colorati',
		},
	];
}
